/**
 * OfflineDB — IndexedDB schema and CRUD helpers для офлайн-режиму
 *
 * Схема (версія 1):
 *   assignments_cache — кеш списку завдань водія (key = endpoint slug)
 *   mutation_queue    — черга мутацій, які не вдалось відправити офлайн
 *
 * Використовує бібліотеку `idb` (Promise-обгортка над IndexedDB API).
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb'

// ── Schema ────────────────────────────────────────────────────────────────────

interface OfflineSchema extends DBSchema {
    assignments_cache: {
        key: string
        value: {
            key: string
            data: unknown
            cachedAt: number
        }
    }
    mutation_queue: {
        key: number
        value: OfflineMutation
    }
}

// ── Types ─────────────────────────────────────────────────────────────────────

export type MutationStatus = 'pending' | 'failed'

/**
 * Офлайн-мутація — одна дія, яка не була відправлена на сервер.
 *
 * type === 'status-change'  → зміна статусу завдання
 * type === 'delay-report'   → повідомлення про затримку (+ фото як Blob)
 * type === 'complete-task'  → завершення завдання з оцінками
 */
export interface OfflineMutation {
    id?: number
    type: 'status-change' | 'delay-report' | 'complete-task'
    uuid: string
    enqueuedAt: number
    retryCount: number
    status: MutationStatus
    lastError?: string
    /** Момент підтвердження дії водієм (ISO) — status-change і complete-task */
    confirmedAt?: string | undefined

    // status-change
    newStatus?: string

    // delay-report
    driverComment?: string
    photos?: Blob[]

    // complete-task
    ratings?: Array<{ ratingDate: string; rating: number; comment: string | null }>
}

// ── DB singleton ──────────────────────────────────────────────────────────────

let _dbPromise: Promise<IDBPDatabase<OfflineSchema>> | null = null

export function openOfflineDB(): Promise<IDBPDatabase<OfflineSchema>> {
    if (!_dbPromise) {
        _dbPromise = openDB<OfflineSchema>('autostrada-offline', 1, {
            upgrade(db) {
                if (!db.objectStoreNames.contains('assignments_cache')) {
                    db.createObjectStore('assignments_cache', { keyPath: 'key' })
                }
                if (!db.objectStoreNames.contains('mutation_queue')) {
                    db.createObjectStore('mutation_queue', {
                        keyPath: 'id',
                        autoIncrement: true,
                    })
                }
            },
        })
    }
    return _dbPromise
}

// ── Assignments cache helpers ─────────────────────────────────────────────────

const ASSIGNMENTS_CACHE_KEY = 'driver-assignments'

export async function cacheAssignments(data: unknown): Promise<void> {
    const db = await openOfflineDB()
    await db.put('assignments_cache', {
        key: ASSIGNMENTS_CACHE_KEY,
        data,
        cachedAt: Date.now(),
    })
}

export async function getCachedAssignments(): Promise<{ data: unknown; cachedAt: number } | undefined> {
    const db = await openOfflineDB()
    return db.get('assignments_cache', ASSIGNMENTS_CACHE_KEY)
}

// ── Mutation queue helpers ────────────────────────────────────────────────────

export async function getAllMutations(): Promise<OfflineMutation[]> {
    const db = await openOfflineDB()
    const all = await db.getAll('mutation_queue')
    return all.sort((a, b) => a.enqueuedAt - b.enqueuedAt)
}

export async function addMutation(mutation: Omit<OfflineMutation, 'id'>): Promise<number> {
    const db = await openOfflineDB()
    const id = await db.add('mutation_queue', mutation as OfflineMutation)
    return id as number
}

export async function updateMutation(mutation: OfflineMutation): Promise<void> {
    const db = await openOfflineDB()
    await db.put('mutation_queue', mutation)
}

export async function deleteMutation(id: number): Promise<void> {
    const db = await openOfflineDB()
    await db.delete('mutation_queue', id)
}
